// The goal's views (Kontext goal, .claude/analysis/kontext-goal-2026-10-06):
//
//   GoalGroupBody — the body of the Kontext home's "Mål" group: per goal its
//                   purpose, the document(s) it answers to, the objectives and
//                   the required terms with coverage, its sub-goals nested
//                   (D56), a count of the sources it draws on (listed under
//                   Källor), and per section "Ask the agent to add …"; or,
//                   without a goal, one invitation to set one. "Continue
//                   toward an earlier goal" offers only goals that have
//                   objectives, with the chats that use them (D56).
//   GoalLens      — the same lists for ONE material: the body of the detail
//                   view's third lens, "Når det målen?". Props only, no Kontext
//                   state, so the detail view can mount it anywhere.
//
// Pure projections of `ChatGoal` values plus intents out. The only local state
// is which objective / term is expanded (view-ephemeral). Status reads by
// SHAPE and a word (full / half / empty mark), never by colour alone; a met
// objective keeps its word for screen readers only so the eye goes to gaps.

import type { MessageDescriptor } from "@lingui/core";
import { msg, plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { SuggestedMark } from "../../../components/AiMarker/AiMarker.js";
import { Button } from "../../../components/Button/Button.js";
import type {
    Claim,
    ClaimIntents,
} from "../../../components/ClaimRow/ClaimRow.js";
import { Icon } from "../../../components/Icon/Icon.js";
import { Menu } from "../../../components/Menu/Menu.js";
import { Text } from "../../../components/Text/Text.js";
import { capRow } from "../../displayLimits.js";
import { DefinitionBacking, GoalKnowledge } from "./GoalKnowledge.js";
import {
    type Ask,
    askOf,
    type ChatGoal,
    type ConceptLinkKind,
    conceptLinkRows,
    type EarlierGoal,
    elsewhereOf,
    type GoalObjective,
    type GoalPart,
    type GoalSummary,
    type GoalTerm,
    type ObjectiveStatus,
    type PartLabel,
    type Passage,
    type Requirement,
    scopedTo,
    summaryOf,
    type TermStatus,
} from "./goal.js";
import "./Goal.css";

export type GoalIntents = {
    // Open a material at one part (reveal + flash it).
    readonly onOpenPart: (part: GoalPart) => void;
    // Open a source at a passage (the requirement's words, a definition).
    readonly onOpenPassage: (passage: Passage) => void;
    // Open a requirement document (from the top).
    readonly onOpenRequirement: (requirement: Requirement) => void;
    // Prefill the chat composer with a request to close a gap (the author
    // sends it). Absent where there is no chat (the detail view elsewhere).
    readonly onAskAgent?: (objective: GoalObjective, ask: Ask) => void;
    // A material's title, to name the part links and "covered elsewhere".
    readonly materialTitle: (materialId: string) => string | undefined;
    // The knowledge bank's claims for a goal (FR-13.9), and what a claim
    // offers (open its passage, ask the agent to correct or remove it).
    readonly claimsOf: (contextId: string) => readonly Claim[];
    readonly claim: ClaimIntents;
};

// Required-term chips shown before "+N" (adversarial design).
const TERM_CHIPS_SHOWN = 8;
// === Status marks ===

type Shape = "full" | "half" | "empty";

const shapeOf: Record<ObjectiveStatus | TermStatus, Shape> = {
    met: "full",
    defined: "full",
    partly: "half",
    used: "half",
    not_yet: "empty",
    missing: "empty",
};

const StatusMark = ({
    status,
}: {
    readonly status: ObjectiveStatus | TermStatus;
}): ReactNode => (
    <span
        className={`bilda-goal-mark bilda-goal-mark--${shapeOf[status]}`}
        aria-hidden="true"
    >
        {shapeOf[status] === "full" ? <Icon name="check" size="xs" /> : null}
    </span>
);

// === Part links ===

const PartName = ({ label }: { readonly label: PartLabel }): ReactNode => {
    const { t } = useLingui();
    switch (label.type) {
        case "question":
            return t`Question ${label.ordinal}`;
        case "section":
            return label.title;
        case "passage":
            return t`“${label.excerpt}”`;
    }
};

const PartLinks = ({
    heading,
    parts,
    showMaterial,
    intents,
}: {
    readonly heading: string;
    readonly parts: readonly GoalPart[];
    readonly showMaterial: boolean;
    readonly intents: GoalIntents;
}): ReactNode =>
    parts.length === 0 ? null : (
        <div className="bilda-goal-links">
            <Text as="span" size="xs" tone="faint">
                {heading}
            </Text>
            <ul className="bilda-rp-uses">
                {parts.map((part) => {
                    const title = intents.materialTitle(part.materialId);
                    return (
                        <li key={`${part.materialId}/${part.elementId}`}>
                            <button
                                type="button"
                                className="bilda-rp-row bilda-rp-use"
                                onClick={() => intents.onOpenPart(part)}
                            >
                                <Icon name="material" size="xs" />
                                <Text as="span" size="xs" tone="dim">
                                    {showMaterial && title !== undefined ? (
                                        <>
                                            <em>{title}</em>
                                            {": "}
                                        </>
                                    ) : null}
                                    <PartName label={part.label} />
                                </Text>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );

const PassageLink = ({
    passage,
    onOpen,
}: {
    readonly passage: Passage;
    readonly onOpen: (p: Passage) => void;
}): ReactNode => {
    const { t } = useLingui();
    const title = passage.sourceTitle;
    const page = passage.page;
    return (
        <button
            type="button"
            className="bilda-rp-row bilda-rp-use"
            onClick={() => onOpen(passage)}
        >
            <Icon name="source" size="xs" />
            <Text as="span" size="xs" tone="dim">
                {page === undefined
                    ? t`From ${title}`
                    : t`From ${title}, p. ${page}`}
            </Text>
        </button>
    );
};

// === Objectives ===

const ObjectiveRow = ({
    objective,
    expanded,
    onToggle,
    showMaterial,
    elsewhere,
    quiet,
    intents,
}: {
    readonly objective: GoalObjective;
    readonly expanded: boolean;
    readonly onToggle: () => void;
    readonly showMaterial: boolean;
    // In one material's lens: the chat's other materials that take it up.
    readonly elsewhere: readonly string[];
    // No material yet: the group says so once, not per objective.
    readonly quiet: boolean;
    readonly intents: GoalIntents;
}): ReactNode => {
    const { t } = useLingui();
    const others = elsewhere.join(", ");
    const word =
        objective.status === "not_yet" && elsewhere.length > 0
            ? t`Not here. Covered in ${others}`
            : objective.status === "met"
              ? t`Met: explained and practised`
              : objective.status === "partly"
                ? objective.explains.length > 0
                    ? t`Partly: explained but not practised`
                    : t`Partly: practised but not explained`
                : t`Not yet: nothing in the material covers it`;
    const ask = askOf(objective);
    const onAskAgent = intents.onAskAgent;
    const askLabel: Record<Ask, string> = {
        add_exercises: t`Ask the agent to add exercises`,
        explain: t`Ask the agent to explain it`,
        cover: t`Ask the agent to cover it`,
    };
    return (
        <li className="bilda-goal-item">
            <button
                type="button"
                className="bilda-rp-row bilda-goal-item__head"
                aria-expanded={expanded}
                onClick={onToggle}
            >
                <StatusMark status={objective.status} />
                <span className="bilda-goal-item__text">
                    <Text as="span" size="sm">
                        {objective.statement}
                    </Text>
                    {objective.suggestion ? <SuggestedMark /> : null}
                    <Text
                        as="span"
                        size="xs"
                        tone="faint"
                        className={
                            objective.status === "met" || quiet
                                ? "bilda-goal-sr-only"
                                : undefined
                        }
                    >
                        {word}
                    </Text>
                </span>
            </button>
            {expanded ? (
                <div className="bilda-goal-item__detail">
                    <PartLinks
                        heading={t`Explained in`}
                        parts={objective.explains}
                        showMaterial={showMaterial}
                        intents={intents}
                    />
                    <PartLinks
                        heading={t`Practised in`}
                        parts={objective.practises}
                        showMaterial={showMaterial}
                        intents={intents}
                    />
                    {objective.from === undefined ? null : (
                        <PassageLink
                            passage={objective.from}
                            onOpen={intents.onOpenPassage}
                        />
                    )}
                    {ask === undefined || onAskAgent === undefined ? null : (
                        <Button
                            size="sm"
                            variant="ghost"
                            className="bilda-goal-ask"
                            onClick={() => onAskAgent(objective, ask)}
                        >
                            {askLabel[ask]}
                        </Button>
                    )}
                </div>
            ) : null}
        </li>
    );
};

// === Terms ===

const useTermWord = (): Record<TermStatus, string> => {
    const { t } = useLingui();
    return {
        defined: t`explained`,
        used: t`used without explanation`,
        missing: t`missing`,
    };
};

const TermDetail = ({
    term,
    claims,
    showMaterial,
    intents,
}: {
    readonly term: GoalTerm;
    // The goal's claims, to show the definition's backing.
    readonly claims: readonly Claim[];
    readonly showMaterial: boolean;
    readonly intents: GoalIntents;
}): ReactNode => {
    const { t } = useLingui();
    const word = useTermWord();
    const def = term.definedIn;
    const title = def?.sourceTitle;
    const page = def?.page;
    const links = conceptLinkRows(term);
    const linkLabel: Record<ConceptLinkKind, string> = {
        broader: t`Under`,
        narrower: t`Narrower`,
        related: t`Related`,
        serves: t`Serves`,
    };
    return (
        <div className="bilda-goal-term">
            <Text as="p" size="sm" weight="medium" className="bilda-goal-flush">
                {term.text}{" "}
                <Text as="span" size="xs" tone="faint">
                    {word[term.status]}
                </Text>
            </Text>
            {term.definition === undefined ? null : (
                <Text as="p" size="sm" className="bilda-goal-flush">
                    {term.definition}
                </Text>
            )}
            <DefinitionBacking
                term={term}
                claims={claims}
                intents={intents.claim}
            />
            {links.map((row) =>
                row.kind === "serves" ? (
                    // Objectives are sentences: one per line.
                    <div key={row.kind} className="bilda-goal-links">
                        <Text as="span" size="xs" tone="faint">
                            {linkLabel[row.kind]}
                        </Text>
                        <ul className="bilda-rp-uses">
                            {row.items.map((statement) => (
                                <li key={statement}>
                                    <Text as="span" size="xs" tone="dim">
                                        {statement}
                                    </Text>
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : (
                    <Text
                        key={row.kind}
                        as="p"
                        size="xs"
                        className="bilda-goal-flush"
                    >
                        <Text as="span" size="xs" tone="faint">
                            {linkLabel[row.kind]}:{" "}
                        </Text>
                        {row.items.join(", ")}
                    </Text>
                ),
            )}
            <PartLinks
                heading={t`Explained in`}
                parts={term.defines}
                showMaterial={showMaterial}
                intents={intents}
            />
            <PartLinks
                heading={t`Used in`}
                parts={term.uses}
                showMaterial={showMaterial}
                intents={intents}
            />
            {def === undefined ? (
                term.definition === undefined ? (
                    <Text
                        as="p"
                        size="xs"
                        tone="faint"
                        className="bilda-goal-flush"
                    >
                        {t`No definition yet.`}
                    </Text>
                ) : null
            ) : (
                <button
                    type="button"
                    className="bilda-goal-definition"
                    onClick={() => intents.onOpenPassage(def)}
                >
                    <Text as="span" size="xs" tone="faint">
                        {t`The source's definition`}
                    </Text>
                    <Text as="span" size="sm" className="bilda-goal-quote">
                        {t`“${def.quote}”`}
                    </Text>
                    <Text as="span" size="xs" tone="dim">
                        {page === undefined ? title : t`${title}, p. ${page}`}
                    </Text>
                </button>
            )}
        </div>
    );
};

const TermChips = ({
    terms,
    claims,
    showMaterial,
    intents,
}: {
    readonly terms: readonly GoalTerm[];
    readonly claims: readonly Claim[];
    readonly showMaterial: boolean;
    readonly intents: GoalIntents;
}): ReactNode => {
    const word = useTermWord();
    const [open, setOpen] = useState<string | null>(null);
    const selected = terms.find((term) => term.id === open);
    // View-ephemeral: the author asked to see every chip.
    const [showAll, setShowAll] = useState(false);
    const row = showAll
        ? { shown: terms, hidden: 0 }
        : capRow(terms, TERM_CHIPS_SHOWN);
    return (
        <>
            <ul className="bilda-goal-chips">
                {row.shown.map((term) => (
                    <li key={term.id}>
                        <button
                            type="button"
                            className="bilda-goal-chip"
                            aria-pressed={open === term.id}
                            aria-label={`${term.text}: ${word[term.status]}`}
                            onClick={() =>
                                setOpen(open === term.id ? null : term.id)
                            }
                        >
                            <StatusMark status={term.status} />
                            <Text
                                as="span"
                                size="xs"
                                className="bilda-goal-chip__text"
                            >
                                {term.text}
                            </Text>
                        </button>
                    </li>
                ))}
                {row.hidden === 0 ? null : (
                    <li>
                        <button
                            type="button"
                            className="bilda-goal-chip"
                            onClick={() => setShowAll(true)}
                        >
                            <Text as="span" size="xs">
                                {`+${row.hidden}`}
                            </Text>
                        </button>
                    </li>
                )}
            </ul>
            {selected === undefined ? null : (
                <TermDetail
                    term={selected}
                    claims={claims}
                    showMaterial={showMaterial}
                    intents={intents}
                />
            )}
        </>
    );
};

// === The two lists, shared by the group and the lens ===

// The sections the author can ask the agent to add to (prefills the composer).
export type AddSection = "objective" | "term" | "audience" | "sub_goal";

const AskToAdd = ({
    section,
    onAsk,
}: {
    readonly section: AddSection;
    readonly onAsk: ((section: AddSection) => void) | undefined;
}): ReactNode => {
    const { t } = useLingui();
    if (onAsk === undefined) return null;
    const label: Record<AddSection, string> = {
        objective: t`Ask the agent to add a learning objective`,
        term: t`Ask the agent to add a term`,
        audience: t`Ask the agent to set who it is for`,
        sub_goal: t`Ask the agent to add a sub-goal`,
    };
    return (
        <Button
            size="sm"
            variant="ghost"
            className="bilda-goal-ask"
            onClick={() => onAsk(section)}
        >
            {label[section]}
        </Button>
    );
};

const CoverageLists = ({
    goal,
    summary,
    showMaterial,
    quiet,
    elsewhere,
    onAskToAdd,
    intents,
}: {
    readonly goal: ChatGoal;
    readonly summary: GoalSummary;
    readonly showMaterial: boolean;
    readonly quiet: boolean;
    // Material scope only: the other materials taking an objective up.
    readonly elsewhere?: (objectiveId: string) => readonly string[];
    // The home group only: "Ask the agent to add …" under each list.
    readonly onAskToAdd?: (section: AddSection) => void;
    readonly intents: GoalIntents;
}): ReactNode => {
    const { t } = useLingui();
    const [open, setOpen] = useState<string | null>(null);
    const {
        objectivesMet: met,
        objectivesPartly: partly,
        objectivesTotal: total,
        termsDefined: defined,
        termsUsed: used,
        termsTotal: terms,
    } = summary;
    const objectivesLine =
        elsewhere === undefined
            ? partly > 0
                ? t`${met} of ${total} met, ${partly} partly`
                : t`${met} of ${total} met`
            : partly > 0
              ? t`Takes up ${met + partly} of ${total}, ${partly} only partly`
              : t`Takes up ${met} of ${total}`;
    const termsLine =
        used > 0
            ? t`${defined} of ${terms} explained, ${used} without explanation`
            : t`${defined} of ${terms} explained`;
    return (
        <>
            {goal.objectives.length === 0 ? null : (
                <>
                    <div className="bilda-goal-sub">
                        <h4 className="bilda-goal-flush">
                            <Text as="span" size="xs" weight="semibold">
                                {t`Learning objectives`}
                            </Text>
                        </h4>
                        <Text as="span" size="xs" tone="dim">
                            {objectivesLine}
                        </Text>
                    </div>
                    <ul className="bilda-rp-list bilda-goal-items">
                        {goal.objectives.map((o) => (
                            <ObjectiveRow
                                key={o.id}
                                objective={o}
                                expanded={open === o.id}
                                onToggle={() =>
                                    setOpen(open === o.id ? null : o.id)
                                }
                                showMaterial={showMaterial}
                                elsewhere={elsewhere?.(o.id) ?? []}
                                quiet={quiet}
                                intents={intents}
                            />
                        ))}
                    </ul>
                </>
            )}
            <AskToAdd section="objective" onAsk={onAskToAdd} />
            {goal.terms.length === 0 ? null : (
                <>
                    <div className="bilda-goal-sub">
                        <h4 className="bilda-goal-flush">
                            <Text as="span" size="xs" weight="semibold">
                                {t`Terms`}
                            </Text>
                        </h4>
                        <Text as="span" size="xs" tone="dim">
                            {termsLine}
                        </Text>
                    </div>
                    <TermChips
                        terms={goal.terms}
                        claims={intents.claimsOf(goal.contextId)}
                        showMaterial={showMaterial}
                        intents={intents}
                    />
                </>
            )}
            <AskToAdd section="term" onAsk={onAskToAdd} />
        </>
    );
};

// === The home group's body ===

export type GoalGroupProps = {
    readonly goals: readonly ChatGoal[];
    readonly hasMaterial: boolean;
    readonly intents: GoalIntents;
    // Prefill the composer: ask the agent to help set the goal.
    readonly onSetGoal: () => void;
    // Prefill the composer: ask the agent to add to one section of a goal.
    readonly onAskToAdd: (goal: ChatGoal, section: AddSection) => void;
    // Goals the chat can continue toward (only goals with objectives).
    readonly earlier: readonly EarlierGoal[];
    readonly onChooseGoal: (contextId: string) => void;
    readonly onRemoveGoal: (contextId: string) => void;
};

// One earlier goal as the picker names it: its name, how many objectives it
// has, and the chats that worked toward it. Pure: descriptors only.
export const earlierGoalLine = (g: EarlierGoal): MessageDescriptor => {
    const chats = g.chats.map((c) => c.title).join(", ");
    const count = g.objectiveCount;
    return g.chats.length === 0
        ? msg`${plural(count, {
              one: "# learning objective",
              other: "# learning objectives",
          })}`
        : msg`${plural(count, {
              one: "# learning objective",
              other: "# learning objectives",
          })}, used in ${chats}`;
};

const ContinueGoal = ({
    earlier,
    onChoose,
}: {
    readonly earlier: readonly EarlierGoal[];
    readonly onChoose: (id: string) => void;
}): ReactNode => {
    const { t, i18n } = useLingui();
    return earlier.length === 0 ? null : (
        <Menu
            trigger={
                <Button size="sm" variant="ghost">
                    {t`Continue toward an earlier goal`}
                </Button>
            }
            items={earlier.map((g) => ({
                _tag: "item",
                label: (
                    <span className="bilda-goal-choice">
                        <Text as="span" size="sm">
                            {g.title}
                        </Text>
                        <Text as="span" size="xs" tone="dim">
                            {i18n._(earlierGoalLine(g))}
                        </Text>
                    </span>
                ),
                onSelect: () => onChoose(g.contextId),
            }))}
        />
    );
};

const GoalBlock = ({
    goal,
    hasMaterial,
    intents,
    onAskToAdd,
    onRemove,
}: {
    readonly goal: ChatGoal;
    readonly hasMaterial: boolean;
    readonly intents: GoalIntents;
    readonly onAskToAdd: (goal: ChatGoal, section: AddSection) => void;
    // Absent for a sub-goal shown nested (it is removed with its parent).
    readonly onRemove?: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const audience = goal.audience.join(", ");
    const inheritedFrom = goal.audienceFrom?.title;
    const sources = goal.sources.length;
    // The roll-up over sub-goals, shown only when there are sub-goals (the
    // own lists below count the goal's own objectives).
    const total = summaryOf([goal]);
    const own: ChatGoal = { ...goal, subGoals: [] };
    const ask = (section: AddSection) => onAskToAdd(goal, section);
    return (
        <div className="bilda-goal-block">
            <div className="bilda-goal-purpose">
                <Text
                    as="p"
                    size="md"
                    weight="semibold"
                    className="bilda-goal-flush"
                >
                    {goal.purpose ?? goal.title}
                </Text>
                {goal.purpose === undefined ? null : (
                    <Text
                        as="p"
                        size="xs"
                        tone="dim"
                        className="bilda-goal-flush"
                    >
                        {goal.title}
                    </Text>
                )}
                {audience.length === 0 ? null : (
                    <Text
                        as="p"
                        size="xs"
                        tone="dim"
                        className="bilda-goal-flush"
                    >
                        {inheritedFrom === undefined
                            ? t`For ${audience}`
                            : t`For ${audience} (from ${inheritedFrom})`}
                    </Text>
                )}
                {goal.subGoals.length === 0 ||
                total.objectivesTotal === 0 ? null : (
                    <Text
                        as="p"
                        size="xs"
                        tone="dim"
                        className="bilda-goal-flush"
                    >
                        {t`With its sub-goals: ${total.objectivesMet} of ${total.objectivesTotal} learning objectives met`}
                    </Text>
                )}
                {sources === 0 ? null : (
                    <Text
                        as="p"
                        size="xs"
                        tone="faint"
                        className="bilda-goal-flush"
                    >
                        {t`${plural(sources, {
                            one: "Draws on # source, listed under Sources",
                            other: "Draws on # sources, listed under Sources",
                        })}`}
                    </Text>
                )}
            </div>
            {goal.requirements.length === 0 ? null : (
                <ul className="bilda-rp-uses">
                    {goal.requirements.map((r) => {
                        const title = r.title;
                        return (
                            <li key={r.sourceId}>
                                <button
                                    type="button"
                                    className="bilda-rp-row bilda-rp-use"
                                    onClick={() => intents.onOpenRequirement(r)}
                                >
                                    <Icon name="source" size="xs" />
                                    <Text as="span" size="xs" tone="dim">
                                        {t`Answers to ${title}`}
                                    </Text>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
            <CoverageLists
                goal={own}
                summary={summaryOf([own])}
                showMaterial
                quiet={!hasMaterial}
                onAskToAdd={ask}
                intents={intents}
            />
            <GoalKnowledge
                claims={intents.claimsOf(goal.contextId)}
                intents={intents.claim}
            />
            {audience.length > 0 && inheritedFrom === undefined ? null : (
                <AskToAdd section="audience" onAsk={ask} />
            )}
            {goal.subGoals.length === 0 ? null : (
                <div className="bilda-goal-subgoals">
                    <h4 className="bilda-goal-flush">
                        <Text as="span" size="xs" weight="semibold">
                            {t`Sub-goals`}
                        </Text>
                    </h4>
                    {goal.subGoals.map((sub) => (
                        <GoalBlock
                            key={sub.contextId}
                            goal={sub}
                            hasMaterial={hasMaterial}
                            intents={intents}
                            onAskToAdd={onAskToAdd}
                        />
                    ))}
                </div>
            )}
            <AskToAdd section="sub_goal" onAsk={ask} />
            {onRemove === undefined ? null : (
                <Button
                    size="sm"
                    variant="ghost"
                    className="bilda-goal-remove"
                    onClick={onRemove}
                >
                    {t`Remove this goal from the chat`}
                </Button>
            )}
        </div>
    );
};

export const GoalGroupBody = ({
    goals,
    hasMaterial,
    intents,
    onSetGoal,
    onAskToAdd,
    earlier,
    onChooseGoal,
    onRemoveGoal,
}: GoalGroupProps): ReactNode => {
    const { t } = useLingui();
    if (goals.length === 0)
        return (
            <div className="bilda-goal-invite">
                <Text
                    as="p"
                    size="sm"
                    weight="medium"
                    className="bilda-goal-flush"
                >
                    {t`What should the chat lead to?`}
                </Text>
                <Text as="p" size="sm" tone="dim" className="bilda-goal-flush">
                    {t`Set learning objectives and terms, preferably from a curriculum, course plan or competence framework. The panel then shows what the material covers and what is missing.`}
                </Text>
                <div className="bilda-goal-invite__actions">
                    <Button size="sm" variant="secondary" onClick={onSetGoal}>
                        {t`Set a goal with the agent`}
                    </Button>
                    <ContinueGoal earlier={earlier} onChoose={onChooseGoal} />
                </div>
            </div>
        );
    return (
        <div className="bilda-goal-body">
            {hasMaterial ? null : (
                <Text
                    as="p"
                    size="xs"
                    tone="faint"
                    className="bilda-goal-flush"
                >
                    {t`No material yet. Everything below remains.`}
                </Text>
            )}
            {goals.map((g) => (
                <GoalBlock
                    key={g.contextId}
                    goal={g}
                    hasMaterial={hasMaterial}
                    intents={intents}
                    onAskToAdd={onAskToAdd}
                    onRemove={() => onRemoveGoal(g.contextId)}
                />
            ))}
            <ContinueGoal earlier={earlier} onChoose={onChooseGoal} />
        </div>
    );
};

// === The detail view's third lens ===

// The lens button's answer for one material, from the same scoping its body
// renders (so the bar and the body never disagree).
export const goalLensRead = (
    goals: readonly ChatGoal[],
    materialId: string,
): GoalSummary => summaryOf(goals.map((g) => scopedTo(g, materialId)));

// Pure: the answer as message descriptors (the caller localizes and joins
// them with ". ").
export const goalLensAnswer = (
    hasGoal: boolean,
    summary: GoalSummary,
): readonly MessageDescriptor[] => {
    if (!hasGoal) return [msg`No goal yet`];
    const taken = summary.objectivesMet + summary.objectivesPartly;
    const total = summary.objectivesTotal;
    const partly = summary.objectivesPartly;
    const used = summary.termsUsed;
    return [
        partly > 0
            ? msg`Takes up ${taken} of ${total} learning objectives, ${partly} only partly`
            : msg`Takes up ${taken} of ${total} learning objectives`,
        ...(used > 0
            ? [
                  msg`${plural(used, {
                      one: "# term without explanation",
                      other: "# terms without explanation",
                  })}`,
              ]
            : []),
    ];
};

export const GoalLens = ({
    goals,
    materialId,
    intents,
    onSetGoal,
}: {
    readonly goals: readonly ChatGoal[];
    readonly materialId: string;
    readonly intents: GoalIntents;
    // Absent where there is no chat to ask.
    readonly onSetGoal?: () => void;
}): ReactNode => {
    const { t } = useLingui();
    if (goals.length === 0)
        return (
            <div className="bilda-goal-invite bilda-goal-invite--flush">
                <Text as="p" size="sm" tone="dim" className="bilda-goal-flush">
                    {t`The chat has no goal yet, so there is nothing to measure the material against.`}
                </Text>
                {onSetGoal === undefined ? null : (
                    <div className="bilda-goal-invite__actions">
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={onSetGoal}
                        >
                            {t`Set a goal with the agent`}
                        </Button>
                    </div>
                )}
            </div>
        );
    return (
        <div className="bilda-goal-body bilda-goal-body--flush">
            {goals.map((g) => {
                const scoped = scopedTo(g, materialId);
                const name = g.purpose ?? g.title;
                return (
                    <div key={g.contextId} className="bilda-goal-block">
                        <Text
                            as="p"
                            size="xs"
                            tone="dim"
                            className="bilda-goal-flush"
                        >
                            {t`Against the goal ${name}. Click a part to see it in the material.`}
                        </Text>
                        <CoverageLists
                            goal={scoped}
                            summary={summaryOf([scoped])}
                            showMaterial={false}
                            quiet={false}
                            elsewhere={(objectiveId) =>
                                elsewhereOf(g, materialId, objectiveId).flatMap(
                                    (id) => {
                                        const title = intents.materialTitle(id);
                                        return title === undefined
                                            ? []
                                            : [title];
                                    },
                                )
                            }
                            intents={intents}
                        />
                    </div>
                );
            })}
        </div>
    );
};
